import { useState, useEffect, useCallback, createContext, useContext, memo, useMemo, useRef } from "react";
import {
  Button,
  ButtonBase, createTheme, Stack, Tab, Tabs,
  LinearProgress, CircularProgress, Box, Grid,
  buttonBaseClasses,
  Radio, RadioGroup, FormControlLabel,
  Paper,
  Typography,
  TableContainer
} from "@mui/material";
import { green, orange } from "@mui/material/colors";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import CalculateIcon from '@mui/icons-material/Calculate';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import TodayIcon from '@mui/icons-material/Today';
import "../InventoryDetail.css";
import { ConditionalWrapper } from "../../../../Components/utils/ConditionalWrapper";
import { ThemeProvider } from "@mui/material/styles";
import ExitSimulationModal from "../../../../Components/DetailsPageLowerLayer/ExitSimulationModal";
import { fetchInventoryTimeline } from "../../../../services/Inventory/InventoryDetailServices";
import { RenderTable } from "./RenderTable";
import { RenderChart } from "./RenderChart";

const theme = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: "#2b0920ff",
          color: "#ffffff",
          borderRadius: "5px",
          paddingTop: "2px",
          paddingBottom: "2px",
          paddingLeft: "8px",
          paddingRight: "8px",
          fontSize: "13px",
          textTransform: "none",
          height: '28px',
          "&:hover": {
            backgroundColor: "#614b5aff"
          }
        }
      }
    }
  }
});

const tabMap = new Map([
  [0, 'Hybrid'],
  [1, 'Weekly'],
  [2, 'Monthly'],
])

const timeframeMap = new Map([
  ["Q1", "Q1FY26"],
  ["Q2", "Q2FY26"],
  ["Q3", "Q3FY26"],
  ["Q4", "Q4FY26"],
  ["Q5", "Q1FY27"]
]);

export function InventoryGraphView({ inventoryId, siteCode, urestrictedStock }) {
  const [isLoading, setIsLoading] = useState({ data: false, error: true });
  const [realTimeLoading, setRealTimeLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [columns, setColumns] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const updateActiveTab = (tab) => {
    setActiveTab(tab);
  }
  const [isActive, setIsActive] = useState({
    table: false,
    chart: false,
    hybrid: true
  });
  const [currentView, setCurrentView] = useState({
    simulate: false,
    realTime: false,
  });
  const [rows, setRows] = useState({});
  // to use this in simulation/view for initialization
  const [editableRows, setEditableRows] = useState({});
  const [showExitModal, setShowExitModal] = useState(false);
  const [timeframe, setTimeframe] = useState("Q1"); // default API value
  const [showTimeOptions, setShowTimeOptions] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [fullRealTimeData, setFullRealTimeData] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    setIsLoading({ data: false });
    setEditableRows({});
    setColumns(['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX']);
  }, [activeTab, currentView.realTime, timeframe]);

  useEffect(() => {
    let isCurrent = true;

    //setIsLoading({ data: false, error: false }); // Hide table, show loader
    if (currentView.realTime) {
      setIsLoading({ data: false, error: false }); // Hide table, show loader
      setRealTimeLoading(true); // keep spinner visible
    } else {
      setRealTimeLoading(false);
    }

    const fetchData = async () => {
      if (currentView.realTime) {
        // Start both calls in parallel
        const [apiResponse, realTimeApiResponse] = await Promise.all([
          fetchInventoryTimeline(
            inventoryId,
            siteCode,
            tabMap.get(activeTab).toUpperCase(),
            timeframeMap.get(timeframe),
            false
          ),
          fetchInventoryTimeline(
            inventoryId,
            siteCode,
            tabMap.get(activeTab).toUpperCase(),
            timeframeMap.get(timeframe),
            true
          )
        ]);

        if (!isCurrent) return; // Skip stale result

        if (apiResponse.error || realTimeApiResponse.error) {
          setIsLoading({ data: false, error: true });
          setRealTimeLoading(true);
          return;
        }

        setFullRealTimeData(realTimeApiResponse.measures);
        setRows(apiResponse.measures);
        setEditableRows(JSON.parse(JSON.stringify(apiResponse.measures)));
        setIsLoading({ data: true, error: false });
        setRealTimeLoading(false);

        if (apiResponse?.headers?.length) {
          const mapped = apiResponse.headers.map(h => {
            const display = h.displayDate || h.displaydate || "";
            const fiscal = h.fiscalLabel || h.fiscallabel || "";
            return display.includes("-") ? `${display.split('-')[0]}-\n${display.split('-')[1]} ${fiscal}` : `${display}\n ${fiscal}`;
          });
          setColumns(mapped);
        }
      } else {
        const apiResponse = await fetchInventoryTimeline(
          inventoryId,
          siteCode,
          tabMap.get(activeTab).toUpperCase(),
          timeframeMap.get(timeframe),
          false
        );
        if (!isCurrent) return;

        if (apiResponse.error) {
          setIsLoading({ data: false, error: true });
          return;
        }
        setRows(apiResponse.measures);
        setEditableRows(JSON.parse(JSON.stringify(apiResponse.measures)));
        setIsLoading({ data: true, error: false });
        setRealTimeLoading(false);

        if (apiResponse?.headers?.length) {
          const mapped = apiResponse.headers.map(h => {
            const display = h.displayDate || h.displaydate || "";
            const fiscal = h.fiscalLabel || h.fiscallabel || "";
            return display.includes("-") ? `${display.split('-')[0]}-\n${display.split('-')[1]} ${fiscal}` : `${display}\n ${fiscal}`;
          });
          setColumns(mapped);
        }
      }
    };
    fetchData();
    return () => {
      isCurrent = false; // mark request as stale
    };
  }, [activeTab, inventoryId, siteCode, timeframe, currentView.realTime, refreshKey]);

  const startCalculation = () => {
    setIsCalculating(true);

    setTimeout(() => {
      setEditableRows(prevRows => {
        const updatedRows = { ...prevRows };
        const numCols = updatedRows.projectedInventory.quantities.length;

        const totalSupplySums = Array(numCols).fill(0);
        const totalDemandSums = Array(numCols).fill(0);

        // Sum only allowed measures under Total Supply
        if (Array.isArray(updatedRows.totalSupply?.subMeasures)) {
          updatedRows.totalSupply.subMeasures.forEach(sub => {
            const subName = sub?.name?.toLowerCase();
            if (subName !== "Planned Arrivals" && subName !== "Predicted Arrivals" &&
              subName !== "Returnable Containers") {
              sub.quantities.forEach((val, colIndex) => {
                totalSupplySums[colIndex] += Number(val || 0);
              });
            }
          });
        }

        // Sum ALL measures under Total Demand
        if (Array.isArray(updatedRows.totalDemand?.subMeasures)) {
          updatedRows.totalDemand.subMeasures.forEach(sub => {
            sub.quantities.forEach((val, colIndex) => {
              totalDemandSums[colIndex] += Number(val || 0);
            });
          });
        }

        updatedRows.totalSupply = {             // Update totals in editableRows so table refreshes
          ...updatedRows.totalSupply,
          quantities: totalSupplySums
        };
        updatedRows.totalDemand = {           // Update totals in editableRows so table refreshes 
          ...updatedRows.totalDemand,
          quantities: totalDemandSums
        };

        const newProjection = [];
        totalSupplySums.forEach((supplyVal, colIndex) => {
          const demandVal = totalDemandSums[colIndex];

          if (colIndex === 0) {
            // First column uses base onHand prop
            newProjection[colIndex] = Number(urestrictedStock || 0) + supplyVal - demandVal;
          } else {
            // Remaining columns use previous Projection value
            newProjection[colIndex] = newProjection[colIndex - 1] + supplyVal - demandVal;
          }
        });

        updatedRows.projectedInventory = {
          ...updatedRows.projectedInventory,
          quantities: newProjection
        };

        return updatedRows;
      });
      setIsCalculating(false);
    }, 1500);
  };

  const handleValueChange = useCallback((rowId, colIndex, value, subRowIndex) => {
    setEditableRows(prev => {
      const updated = { ...prev };

      if (typeof subRowIndex === "number") {
        // Update nested subMeasures quantities
        updated[rowId].subMeasures[subRowIndex].quantities[colIndex] = value;
        updated[rowId].subMeasures[subRowIndex].isEdited = true;
        updated[rowId].isSubEdited = true;
      } else {
        // Update top-level quantities
        updated[rowId].quantities[colIndex] = value;
        updated[rowId].isEdited = true;
      }
      return updated;
    });
  }, []);

  const filterOutSubMeasures = (data) => {
    const copy = { ...data };

    Object.keys(copy).forEach(key => {
      if (Array.isArray(copy[key]?.subMeasures)) {
        copy[key] = {
          ...copy[key],
          subMeasures: copy[key].subMeasures.filter(
            sm => {
              const name = sm?.name?.toLowerCase();
              return name !== "planned arrivals" && name !== "predicted arrivals" && name !== "returnable containers";
            }
          )
        };
      }
    });
    return copy;
  };

  const onTimeframeChange = (value) => {
    setTimeframe(value)
    setShowTimeOptions(false)
  }

  const TimeframeOptions = () => {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, position: "sticky", left: 0 }}>
        <Paper
          elevation={3}
          sx={{
            paddingLeft: 2, paddingBottom: 0.5,
            backgroundColor: "#fafafa",
            border: "1px solid #ccc",
            borderRadius: "8px"
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ mb: 0.7, fontWeight: "bold", color: "#6d6c6cff" }}
          >
            Select Timeframe
          </Typography>

          <RadioGroup
            row
            onChange={(e) => onTimeframeChange(e.target.value)}
            sx={{ flexDirection: "row", flexWrap: "nowrap" }}
          >
            {Array.from(timeframeMap.entries()).map((opt) => (
              <FormControlLabel
                checked={timeframe === opt[0]}
                key={opt[0]}
                value={opt[0]}
                control={
                  <Radio
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                      p: 0,
                      color: "#969393ff",
                      "&.Mui-checked": { color: "#000" }
                    }}
                  />
                }
                label={opt[1]}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.8rem",
                    color: "#000",
                    marginLeft: 0.2
                  },
                  marginRight: 2.5,
                }}
              />
            ))}
          </RadioGroup>
        </Paper>
      </Box>
    )
  }
  return (
    <div style={{ width: '100%' }} ref={ref}>
      <ThemeProvider theme={theme}>
        <Grid container sx={{ marginBottom: '10px', position: "sticky", left: 0, alignItems: 'center', mr: 2 }}>
          <Grid size={{ xs: 4 }}>
            <Stack direction="row" gap={1}>
              <ConditionalWrapper condition={!currentView.simulate && !currentView.realTime}>
                <Button onClick={() => {
                  setCurrentView({ realTime: true, simulate: false });
                }}
                  disabled={currentView.realTime}>
                  Real Time
                  <AccessTimeIcon sx={{ marginLeft: 1 }} />
                </Button>
              </ConditionalWrapper>
              <ConditionalWrapper condition={!currentView.simulate && currentView.realTime}>
                <Button
                  onClick={() => {
                    setCurrentView({ realTime: true, simulate: false });
                    setRefreshKey(prev => prev + 1);
                  }}>
                  Refresh
                  <CachedRoundedIcon sx={{ marginLeft: 0.5 }} />
                </Button>
                <Button
                  onClick={() => {
                    setCurrentView({ realTime: false, simulate: false });
                    setRealTimeLoading(false);
                  }}>
                  <ClearOutlinedIcon sx={{ marginLeft: 0.5 }} />
                </Button>
              </ConditionalWrapper>
              {/* Simulate button (initial state) */}
              <ConditionalWrapper condition={!currentView.simulate}>
                <Button
                  onClick={() => {
                    setCurrentView({ simulate: true, realTime: false });
                  }}
                >
                  Simulate
                  <ViewInArIcon sx={{ marginLeft: 1 }} />
                </Button>
              </ConditionalWrapper>
              {/* Simulation active state: Calculate + Exit Simulation */}
              <ConditionalWrapper condition={currentView.simulate && !currentView.realTime}>
                <Button sx={{
                  "&.Mui-disabled": {
                    backgroundColor: "#c4c0c0ff",
                    color: "#000000"
                  }
                }}
                  onClick={() => {
                    //setIsActive({ realTime: false, simulate: true });
                    startCalculation();
                  }}
                  disabled={!isLoading.data} >
                  Calculate
                  <CalculateIcon sx={{ marginLeft: 1 }} />
                </Button>
                <Button
                  onClick={() => {
                    //setIsActive({ realTime: false, simulate: false, hybrid: true });
                    setShowExitModal(true);
                  }}>
                  <ClearOutlinedIcon />
                </Button>
              </ConditionalWrapper>
            </Stack>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Stack sx={{ justifySelf: 'center', width: '100%' }}>
              <Tabs
                value={activeTab}
                onChange={(e, tab) => updateActiveTab(tab)}
                sx={{
                  backgroundColor: 'black',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: 3,
                  minHeight: '30px',
                  [`.${buttonBaseClasses.root}`]: {
                    minHeight: '30px',
                    lineHeight: '1',
                    // borderRight: '1px solid #e0e0e0 '
                  }
                }}
                slotProps={{
                  indicator: {
                    sx: {
                      backgroundColor: 'transparent'
                    }
                  },
                  list: {
                    sx: {
                      justifyContent: 'center',
                    }
                  },
                }}
                variant='fullWidth'
                textColor="inherit"
                aria-label="chart-data-tab"
              >
                <Tab label="Hybrid" />
                <Tab label="Weekly" />
                <Tab label="Monthly" />
              </Tabs>
            </Stack>
          </Grid>
          <Grid container size={{ xs: 4 }} justifyContent='flex-end'>
            <Stack direction="row-reverse" columnGap={2}>
              <ButtonBase
                onClick={() =>
                  setIsActive({ table: true })
                }
              >
                <GridOnRoundedIcon color={isActive.table ? "primary" : ""} />
              </ButtonBase>
              <ButtonBase
                onClick={() =>
                  setIsActive({ chart: true })
                }
              >
                <TimelineRoundedIcon color={isActive.chart ? "primary" : ""} />
              </ButtonBase>
              <ButtonBase
                onClick={() =>
                  setIsActive({ hybrid: true })
                }
              >
                <QueryStatsIcon color={isActive.hybrid ? "primary" : ""} />
              </ButtonBase>
              <ButtonBase
                //variant="outlined"
                size="large"
                onClick={() => setShowTimeOptions(!showTimeOptions)}
              // sx={{
              //   borderColor: "#808080",
              //   color: "#000",
              //   backgroundColor: "#f5f5f5",
              //   "&:hover": { backgroundColor: "#d3d3d3" }
              // }}
              >
                <TodayIcon color={showTimeOptions ? 'primary' : ''} />
              </ButtonBase>
            </Stack>
          </Grid>
        </Grid>
        <ConditionalWrapper condition={showTimeOptions}>
          <TimeframeOptions />
        </ConditionalWrapper>
      </ThemeProvider>

      {/* Show linear progress bar at top when calculating */}
      <ConditionalWrapper condition={isCalculating}>
        <Stack sx={{
          width: '100%', my: 5,
          "& .MuiLinearProgress-bar": {
            backgroundColor: "#545454ff"  // grey
          },
        }} spacing={2}>
          <LinearProgress />
          <LinearProgress />
          <LinearProgress />
        </Stack>
      </ConditionalWrapper>

      {/* Show circular progress when fetching real-time data */}
      <ConditionalWrapper condition={currentView.realTime && realTimeLoading}>
        <CircularProgress size={48} thickness={4} sx={{ color: "#7d7c7cff", my: 5 }} />
      </ConditionalWrapper>

      <HighlightMarkProvider>
        <TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
          <ConditionalWrapper condition={
            !isCalculating && !currentView.realTime && (isActive.hybrid || isActive.chart || currentView.simulate)
          }>
            <RenderChart
              isLoading={isLoading}
              xSeries={columns}
              ySeries={[
                {
                  label: 'Min Stock',
                  data: !editableRows.minStock ? [] : editableRows.minStock?.quantities?.map(d => Number(d)),
                  id: 'minstock',
                  color: orange[500],
                  area: false,
                  stack: true,
                  showMark: false,
                  connectNulls: true
                },
                {
                  label: 'Projection',
                  data: !editableRows?.projectedInventory ? [] : editableRows?.projectedInventory?.quantities?.map(d => Number(d)),
                  id: 'projection',
                  color: green[500],
                  area: false,
                  showMark: true,
                  connectNulls: true,
                },
                {
                  label: 'Max Stock',
                  data: !editableRows.maxStock ? [] : editableRows.maxStock.quantities
                    .map((_, i) => Number(editableRows.maxStock.quantities[i]) - Number(editableRows.minStock.quantities[i])),
                  valueFormatter: (_, context) => !editableRows ? 0 : Number(editableRows.maxStock.quantities[context.dataIndex]),
                  id: 'maxstock',
                  color: orange[500],
                  area: true,
                  stack: true,
                  showMark: false,
                  connectNulls: true
                }
              ]}
              height={isActive.chart ? 600 : 300}
              width={ref.current ? ref.current.clientWidth : 0}
            />
          </ConditionalWrapper>

          <ConditionalWrapper condition={!isCalculating && isLoading.data && (isActive.table || isActive.hybrid || (currentView.simulate))}>
            <RenderTable
              columns={columns}
              rows={filterOutSubMeasures(rows)}               // base rows (normal or normal snapshot)
              editedRows={filterOutSubMeasures(editableRows)}
              currentView={currentView}
              handleValueChange={handleValueChange}
              fullData={fullRealTimeData}                     // complete realtime dataset for lookups
            />
          </ConditionalWrapper>
        </TableContainer>
        <ExitSimulationModal
          open={showExitModal}
          handleClose={() => setShowExitModal(false)}
          onConfirm={() => {
            setCurrentView({ simulate: false, realTime: false });
            setEditableRows(JSON.parse(JSON.stringify(rows)))
            setShowExitModal(false);
          }}
          theme={theme}
        />
      </HighlightMarkProvider>
    </div >
  );
}

const HighlightMarkContext = createContext({
  highlightMMarkIdx: -1,
  setHighlightMarkIdx: () => { }
})

export const useHighlightMarkIdx = () => useContext(HighlightMarkContext)

const HighlightMarkProvider = memo(({ children }) => {
  const [highlightMarkIdx, setHighlightMarkIdx] = useState(-1)
  const updateHighlightMarkIdx = useCallback((idx) => setHighlightMarkIdx(idx), [])
  const contextValue = useMemo(() => ({ highlightMarkIdx, setHighlightMarkIdx: updateHighlightMarkIdx }), [highlightMarkIdx, updateHighlightMarkIdx]);

  return (
    <HighlightMarkContext.Provider value={contextValue}>
      {children}
    </HighlightMarkContext.Provider>
  )
})